/**
 * The patient's document library list (Documents subtab).
 *
 * Lists the patient's editable library documents (`markdown` only — `blueprint`
 * documents are retired in Phase 5a; the Blueprint is now card-graph-rendered),
 * lets the provider create a new markdown document, and selects one for
 * editing. Source documents live in the Sources subtab, not here. Titles are
 * non-PHI metadata.
 */
import React, { useState } from "react";
import type { BlueprintDocument } from "../../types";

/** Document types shown in the editable library (not `source`/`entity`/`blueprint`). */
// eslint-disable-next-line react-refresh/only-export-components
export const LIBRARY_TYPES = new Set(["markdown"]);

/** Props for {@link DocumentLibrary}. */
interface DocumentLibraryProps {
  /** All of the patient's documents. */
  documents: BlueprintDocument[];
  /** The currently selected document id, if any. */
  selectedId: string | null;
  /** Select a document for editing. */
  onSelect: (documentId: string) => void;
  /** Create a new markdown document with the given title. */
  onCreate: (title: string) => void;
  /** Rename a document to the given title. */
  onRename: (documentId: string, title: string) => void;
  /** Delete a document (and its versions/drafts). */
  onDelete: (documentId: string) => void;
  /** Filter query for documents by title. */
  searchQuery?: string;
  /** Clear search query callback. */
  onClearSearch?: () => void;
  /** Sort field: created_at date or alphabetical title. */
  sortField?: "date" | "alphabetical";
  /** Sort direction: ascending or descending. */
  sortDirection?: "asc" | "desc";
  /** Whether the create document input box is open. */
  isCreateOpen?: boolean;
}

/** The new-document title input + create button. */
function CreateBox({
  onCreate,
}: {
  onCreate: (title: string) => void;
}): React.ReactElement {
  const [title, setTitle] = useState("");
  const submit = (): void => {
    const trimmed = title.trim();
    if (trimmed === "") return;
    onCreate(trimmed);
    setTitle("");
  };
  return (
    <div className="flex gap-2 mb-3">
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="New document title"
        aria-label="New document title"
        className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm"
      />
      <button
        type="button"
        onClick={submit}
        className="px-3 py-1 bg-slate-800 text-white rounded text-sm hover:bg-slate-700"
      >
        New
      </button>
    </div>
  );
}

/** Props for {@link LibraryRow}. */
interface LibraryRowProps {
  /** The document this row represents. */
  doc: BlueprintDocument;
  /** Whether this row is the selected document. */
  selected: boolean;
  /** Select this document for editing. */
  onSelect: (documentId: string) => void;
  /** Rename this document to the given title. */
  onRename: (documentId: string, title: string) => void;
  /** Delete this document. */
  onDelete: (documentId: string) => void;
}

/** A single library row: select button + inline-rename + delete affordance. */
function LibraryRow({
  doc,
  selected,
  onSelect,
  onRename,
  onDelete,
}: LibraryRowProps): React.ReactElement {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(doc.title);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const save = (): void => {
    const trimmed = title.trim();
    if (trimmed !== "" && trimmed !== doc.title) onRename(doc.id, trimmed);
    setEditing(false);
  };
  const startEditing = (): void => {
    setTitle(doc.title);
    setEditing(true);
  };

  if (editing) {
    return (
      <li data-testid="library-row-editing">
        <div className="flex gap-2 items-center rounded-lg px-3 py-2 border border-blue-300 bg-blue-50">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            aria-label={`Rename ${doc.title}`}
            data-testid="rename-input"
            className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm"
          />
          <button
            type="button"
            onClick={save}
            data-testid="rename-save"
            className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
          >
            Save
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            data-testid="rename-cancel"
            className="px-2 py-1 text-slate-600 rounded text-xs hover:bg-slate-100"
          >
            Cancel
          </button>
        </div>
      </li>
    );
  }

  return (
    <li className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => onSelect(doc.id)}
        aria-pressed={selected}
        className={`flex-1 text-left rounded-lg px-3 py-2 ${
          selected
            ? "bg-blue-50 border border-blue-300"
            : "hover:bg-slate-50 border border-transparent"
        }`}
      >
        <div className="text-sm font-medium text-slate-800">{doc.title}</div>
        <div className="text-[11px] uppercase tracking-wide text-slate-400">
          {doc.type}
        </div>
      </button>
      <button
        type="button"
        onClick={startEditing}
        aria-label="Rename document"
        data-testid="rename-button"
        className="px-2 py-1 text-xs text-slate-500 hover:text-slate-800"
      >
        Rename
      </button>
      {/*
       * BLA-315 / ADR-069: the profile document is not deletable in this
       * first rollout — the server refuses the delete regardless
       * (`_guard_undeletable_document` in `backend/app/routers/documents.py`),
       * so the affordance is hidden here to avoid inviting a failed attempt.
       * To lift: drop this `doc.is_profile` check and the matching backend
       * guard branch together (see ADR-069).
       */}
      {doc.is_profile ? null : confirmingDelete ? (
        <>
          <button
            type="button"
            onClick={() => onDelete(doc.id)}
            aria-label={`Confirm delete ${doc.title}`}
            data-testid="delete-confirm"
            className="px-2 py-1 text-xs text-rose-700 font-semibold hover:text-rose-900"
          >
            Confirm
          </button>
          <button
            type="button"
            onClick={() => setConfirmingDelete(false)}
            aria-label="Cancel delete"
            data-testid="delete-cancel"
            className="px-2 py-1 text-xs text-slate-500 hover:text-slate-800"
          >
            Cancel
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={() => setConfirmingDelete(true)}
          aria-label="Delete document"
          data-testid="delete-button"
          className="px-2 py-1 text-xs text-rose-600 hover:text-rose-800"
        >
          Delete
        </button>
      )}
    </li>
  );
}

/**
 * Render the document library list with create, select, rename, delete, search, and sort.
 *
 * @param props - The documents, selection, handlers, search query, sort controls, and create toggle.
 * @returns The library element.
 */
export const DocumentLibrary: React.FC<DocumentLibraryProps> = ({
  documents,
  selectedId,
  onSelect,
  onCreate,
  onRename,
  onDelete,
  searchQuery = "",
  onClearSearch,
  sortField = "date",
  sortDirection = "desc",
  isCreateOpen = true,
}) => {
  const library = documents.filter((d) => LIBRARY_TYPES.has(d.type));
  const normalizedQuery = searchQuery.trim().toLowerCase();

  const filtered = library.filter((doc) =>
    normalizedQuery === ""
      ? true
      : doc.title.toLowerCase().includes(normalizedQuery),
  );

  const sorted = [...filtered].sort((a, b) => {
    const cmp =
      sortField === "alphabetical"
        ? a.title.localeCompare(b.title)
        : (a.created_at ? new Date(a.created_at).getTime() : 0) -
          (b.created_at ? new Date(b.created_at).getTime() : 0);
    return sortDirection === "asc" ? cmp : -cmp;
  });

  return (
    <div>
      {isCreateOpen && <CreateBox onCreate={onCreate} />}
      {normalizedQuery !== "" && sorted.length === 0 ? (
        <div
          className="py-2 text-sm text-slate-500"
          data-testid="no-documents-matching"
        >
          <p className="italic">
            No documents matching &lsquo;{searchQuery}&rsquo;
          </p>
          {onClearSearch && (
            <button
              type="button"
              onClick={onClearSearch}
              className="mt-1 text-xs text-blue-600 hover:text-blue-800 underline"
            >
              Clear search
            </button>
          )}
        </div>
      ) : library.length === 0 ? (
        <p className="text-sm text-slate-400 italic">No documents yet.</p>
      ) : (
        <ul className="space-y-1" data-testid="document-library">
          {sorted.map((doc) => (
            <LibraryRow
              key={doc.id}
              doc={doc}
              selected={doc.id === selectedId}
              onSelect={onSelect}
              onRename={onRename}
              onDelete={onDelete}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

export default DocumentLibrary;
